import { FolderPlus } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { ErrorState, useErrorMessage } from '@nucleus-commerce/admin-kit/errors';
import { isApiError } from '@nucleus-commerce/api-client/transport';
import { Button } from '@nucleus-commerce/ui/components/button';
import { Checkbox } from '@nucleus-commerce/ui/components/checkbox';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@nucleus-commerce/ui/components/dialog';
import { Input } from '@nucleus-commerce/ui/components/input';
import { Skeleton } from '@nucleus-commerce/ui/components/skeleton';

import { useCreateMediaFolder } from '../hooks/use-media-folder-mutations';
import { useMediaFolders } from '../hooks/use-media-folders';

import { MAX_FOLDER_NAME_LENGTH } from './folder-name-dialog';

export interface FolderPickerDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  description: string;
  confirmLabel: string;
  /** Folders ticked when the dialog opens. */
  initialSelected?: ReadonlySet<string>;
  pending?: boolean;
  onConfirm: (folderIds: string[]) => void;
}

/** Tick the folders a file (or a selection of files) should sit in; a new folder can be made inline. */
export function FolderPickerDialog({ open, onOpenChange, ...body }: FolderPickerDialogProps) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        {/* Mounted only while open, so every opening starts from the current folder set. */}
        {open ? <FolderPickerBody onOpenChange={onOpenChange} {...body} /> : null}
      </DialogContent>
    </Dialog>
  );
}

function FolderPickerBody({
  onOpenChange,
  title,
  description,
  confirmLabel,
  initialSelected,
  pending = false,
  onConfirm,
}: Omit<FolderPickerDialogProps, 'open'>) {
  const { t } = useTranslation(['media', 'common']);
  const folders = useMediaFolders();
  const create = useCreateMediaFolder();
  const message = useErrorMessage();
  const [selected, setSelected] = useState<Set<string>>(() => new Set(initialSelected));
  const [newName, setNewName] = useState('');

  const toggle = (id: string, checked: boolean) =>
    setSelected((prev) => {
      const next = new Set(prev);
      if (checked) next.add(id);
      else next.delete(id);
      return next;
    });

  const createFolder = () => {
    const name = newName.trim();
    if (name === '' || create.isPending) return;
    create.mutate(name, {
      onSuccess: (folder) => {
        setNewName('');
        setSelected((prev) => new Set(prev).add(folder.id));
      },
    });
  };
  const createError = create.error;
  const nameTaken = isApiError(createError) && createError.code === 'ALREADY_EXISTS';

  return (
    <>
      <DialogHeader>
        <DialogTitle>{title}</DialogTitle>
        <DialogDescription>{description}</DialogDescription>
      </DialogHeader>
      {folders.isError ? (
        <ErrorState error={folders.error} onRetry={() => void folders.refetch()} />
      ) : folders.isPending ? (
        <div className="flex flex-col gap-2">
          {Array.from({ length: 3 }).map((_, i) => (
            <Skeleton key={i} className="h-8 w-full" />
          ))}
        </div>
      ) : folders.data.length === 0 ? (
        <p className="text-muted-foreground text-sm">{t('media:folders.noneYet')}</p>
      ) : (
        <ul
          className="flex max-h-72 flex-col gap-1 overflow-y-auto"
          aria-label={t('media:folders.title')}
        >
          {folders.data.map((folder) => {
            const id = `media-folder-pick-${folder.id}`;
            return (
              <li key={folder.id}>
                <label
                  htmlFor={id}
                  className="hover:bg-muted flex cursor-pointer items-center gap-3 rounded-md px-2 py-1.5 text-sm"
                >
                  <Checkbox
                    id={id}
                    checked={selected.has(folder.id)}
                    onCheckedChange={(checked) => toggle(folder.id, checked === true)}
                  />
                  <span className="flex-1 truncate">{folder.name}</span>
                  <span className="text-muted-foreground text-xs tabular-nums">
                    {t('media:folders.count', { count: folder.itemCount })}
                  </span>
                </label>
              </li>
            );
          })}
        </ul>
      )}
      <form
        className="flex flex-col gap-1"
        onSubmit={(event) => {
          event.preventDefault();
          createFolder();
        }}
      >
        <div className="flex gap-2">
          <Input
            value={newName}
            maxLength={MAX_FOLDER_NAME_LENGTH}
            placeholder={t('media:folders.namePlaceholder')}
            aria-label={t('media:folders.nameLabel')}
            onChange={(event) => setNewName(event.target.value)}
          />
          <Button
            type="submit"
            variant="outline"
            disabled={newName.trim() === '' || create.isPending}
          >
            <FolderPlus aria-hidden />
            {t('media:folders.new')}
          </Button>
        </div>
        {createError ? (
          <p className="text-destructive text-sm" role="alert">
            {nameTaken ? t('media:folders.nameTaken') : message(createError)}
          </p>
        ) : null}
      </form>
      <DialogFooter>
        <Button type="button" variant="ghost" onClick={() => onOpenChange(false)}>
          {t('common:actions.cancel')}
        </Button>
        <Button type="button" disabled={pending} onClick={() => onConfirm([...selected])}>
          {confirmLabel}
        </Button>
      </DialogFooter>
    </>
  );
}
